import React, { useState, useEffect, useCallback, useRef } from "react";
import { Trash2, Edit2, X, FilterX, Clock, XCircle, UserX, CheckCircle, CalendarClock, Ban } from 'lucide-react';
import "../../styles/components/_scheduling.scss";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Tooltip from "../../components/tootip/Tootip";
import ScheduleVisitProvider from "../../infra/provider/scheduleVisitProvider";
import { debounce } from "lodash";
import { Formatter } from "../../utils/formatter";
import EnterpriseProvider from "../../infra/provider/enterpriseProvider";
import Enterprise from "../../domain/entities/enterprise";
import { ListSearch } from "../../domain/entities/valueObjects/listSearch";
import { CANCELED, CANCELED_LABEL, COMPLETED, COMPLETED_LABEL, NO_SHOW, NO_SHOW_LABEL, PENDING, PENDING_LABEL, RESCHEDULED, RESCHEDULED_LABEL, UNREACHABLE, UNREACHABLE_LABEL } from "./SchedulingConstants";

interface TableData {
  id: string;
  createdAt: string;
  date: string;
  time: string;
  leadName: string;
  phone: string;
  enterpriseName: string;
  observation: string;
  status: string;
  responsible: {
    id: string;
    email: string;
    name: string;
  }
}

export function Scheduling() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEnterprise, setSelectedLocation] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [data, setData] = useState<TableData[]>([]);
  const [filteredData, setFilteredData] = useState<TableData[]>([]);
  const isFetching = useRef(false);
  const isFetchEnterprise = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);

  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<TableData | null>(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    date: "",
    time: "",
  });

  const scheduleVisitsProvider = ScheduleVisitProvider.getInstance();
  const enterpriseProvider = EnterpriseProvider.getInstance();

  const getEnterprises = async () => {
    if (isFetchEnterprise.current) return;
    isFetchEnterprise.current = true;
    const enterprises = await enterpriseProvider.getEnterprisesName();
    setEnterprises(enterprises);
    isFetchEnterprise.current = false;
  };

  const getScheduledVisits = useCallback(
    async (
      pageNumber = 1,
      searchTerm = "",
      selectedDate = "",
      selectedEnterprise = ""
    ) => {
      if (isFetching.current) return;
      isFetching.current = true;
      setIsLoading(true);
      const search = new ListSearch({
        searchTerm: searchTerm,
        status: selectedStatus,
        orderBy: "createdAt",
        limit: itemsPerPage,
        offset: (pageNumber - 1) * itemsPerPage,
        fromDate: selectedDate ? Formatter.dateStringToTimestamp(selectedDate) : undefined,
        toDate: selectedDate ? Formatter.dateStringToTimestamp(selectedDate) : undefined,
        enterpriseId: selectedEnterprise ? parseInt(selectedEnterprise) : 0,
      });
      try {
        const response =
          await scheduleVisitsProvider.getScheduledVisits(search);
        const mappedData = response.scheduleVisits.map((visit) => ({
          id: visit.id,
          createdAt: Formatter.timestampToDateBR(visit.createdAt),
          date: Formatter.timestampToDateBR(visit.date, false),
          time: Formatter.timestampToTime(visit.date),
          leadName: visit.requester ? visit.requester.name : "",
          phone: visit.requester ? visit.requester.phone : "",
          enterpriseName: visit.enterpriseName,
          observation: visit.status === RESCHEDULED ? (visit.correspondentObs || visit.obs) : "Sem observações",
          status: visit.status,
          responsible: {
            id: visit.responsible?.id ?? "",
            email: visit.responsible?.email ?? "",
            name: visit.responsible?.name ?? ""
          }
        }));
        setData(mappedData);
        setTotalItems(response.totalItems);
      } catch (error) {
        toast.error("Erro ao carregar visitas agendadas");
        console.error(error);
      } finally {
        isFetching.current = false;
        setIsLoading(false);
      }
    },
    [scheduleVisitsProvider, itemsPerPage, selectedStatus]
  );

  useEffect(() => {
    getScheduledVisits(currentPage, searchTerm, selectedDate, selectedEnterprise);
    getEnterprises();
  }, [itemsPerPage, currentPage, getScheduledVisits, searchTerm, selectedDate, selectedEnterprise]);

  useEffect(() => {
    if (selectedEnterprise) {
      const selectedEnterpriseName = enterprises.find(e => e.id.toString() === selectedEnterprise)?.name;

      if (selectedEnterpriseName) {
        const filtered = data.filter(visit => {
          const enterpriseMatch = visit.enterpriseName.trim().toLowerCase() === selectedEnterpriseName.trim().toLowerCase();
          const searchMatch = searchTerm === "" ||
            visit.responsible.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            visit.enterpriseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            visit.observation.toLowerCase().includes(searchTerm.toLowerCase()) ||
            translateStatus(visit.status).toLowerCase().includes(searchTerm.toLowerCase());

          return enterpriseMatch && searchMatch;
        });
        setFilteredData(filtered);
      } else {
        setFilteredData(data);
      }
    } else {
      // Se não houver empreendimento selecionado, filtra apenas pela busca
      const filtered = data.filter(visit => {
        return searchTerm === "" ||
          visit.responsible.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          visit.enterpriseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          visit.observation.toLowerCase().includes(searchTerm.toLowerCase()) ||
          translateStatus(visit.status).toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredData(filtered);
    }
  }, [selectedEnterprise, data, enterprises, searchTerm]);

  const handleSearchTermChange = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    getScheduledVisits(1, searchTerm, selectedDate, selectedEnterprise);
  };

  const handleSearchDateChange = (date: string) => {
    setSelectedDate(date);
    debouncedDate(date);
  };

  const handleSearchLocationChange = (location: string) => {
    setSelectedLocation(location);
    getScheduledVisits(1, searchTerm, selectedDate, location);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedDate("");
    setSelectedLocation("");
    getScheduledVisits(1, "", "", "");
  };

  const formatphone = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    } else if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const translateStatus = (status: string): string => {
    switch (status) {
      case PENDING:
        return PENDING_LABEL;
      case CANCELED:
        return CANCELED_LABEL;
      case NO_SHOW:
        return NO_SHOW_LABEL;
      case COMPLETED:
        return COMPLETED_LABEL;
      case RESCHEDULED:
        return RESCHEDULED_LABEL;
      case UNREACHABLE:
        return UNREACHABLE_LABEL;
      default:
        return status;
    }
  };

  const handleStatusChange = async (data: TableData, newStatus: string) => {
    if (newStatus === RESCHEDULED) {
      setSelectedVisit(data);
      setShowRescheduleModal(true);
      return;
    }

    try {
      await scheduleVisitsProvider.updateScheduledVisitStatusById(data.id, newStatus, data.observation);
      toast.success("Status alterado com sucesso!")
    } catch (error) {
      toast.error("Erro ao atualizar status.");
    }
    finally {
      getScheduledVisits();
    }
  }

  const handleReschedule = async () => {
    if (!selectedVisit) return;

    try {
      const newDate = `${rescheduleForm.date}T${rescheduleForm.time}:00`;
      const timestamp = new Date(newDate).getTime() / 1000;
      const formattedDate = Formatter.timestampToDateBR(timestamp);
      const formattedTime = Formatter.timestampToTime(timestamp);
      const newObservation = `Reagendado para: ${formattedDate} às ${formattedTime} (Nova data)`;

      await scheduleVisitsProvider.updateScheduledVisitStatusById(
        selectedVisit.id,
        RESCHEDULED,
        newObservation,
        timestamp
      );
      toast.success("Visita reagendada com sucesso!");
      setShowRescheduleModal(false);
      setRescheduleForm({ date: "", time: "" });
      setSelectedVisit(null);
    } catch (error) {
      toast.error("Erro ao reagendar visita.");
    } finally {
      getScheduledVisits();
    }
  }

  const debouncedDate = useRef(
    debounce((date) => {
      getScheduledVisits(1, searchTerm, date, selectedEnterprise);
    }, 600)
  ).current;

  return (
    <div className="container-scheduling">
      <div className="search-section-scheduling">
        <input
          type="text"
          placeholder="Busque pelo nome do lead, empreendimento, observação ou status."
          className="search-input-scheduling"
          value={searchTerm}
          onChange={(e) => handleSearchTermChange(e.target.value)}
        />
        <input
          type="date"
          className="search-input-scheduling"
          placeholder="Selecione a data"
          value={selectedDate}
          onChange={(e) => handleSearchDateChange(e.target.value)}
        />
        <select
          className="search-select-scheduling"
          value={selectedEnterprise}
          onChange={(e) => handleSearchLocationChange(e.target.value)}
        >
          <option value="">Selecione um empreendimento</option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.id} value={enterprise.id}>
              {enterprise.name}
            </option>
          ))}
        </select>
        <Tooltip text="Limpar filtros">
          <button className="clear-filters-button" onClick={handleClearFilters}>
            <FilterX size={16} />
          </button>
        </Tooltip>
      </div>
      <table className="table-scheduling">
        <thead>
          <tr>
            <th>Data da visita</th>
            <th>Horário</th>
            <th>Nome do lead</th>
            <th>Empreendimento</th>
            {/* <th>Celular</th> */}
            <th>Observação</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                Carregando...
              </td>
            </tr>
          ) : filteredData.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                Nenhum resultado encontrado.
              </td>
            </tr>
          ) : (
            filteredData.map((visit) => (
              <tr key={visit.id}>
                <td>{visit.date}</td>
                <td>{visit.time}</td>
                <td>{visit.responsible.name}</td>
                <td>{visit.enterpriseName}</td>
                {/* <td>{formatphone(visit.phone)}</td> */}
                <td>
                  {visit.observation}
                </td>
                <td>{translateStatus(visit.status)}</td>
                <td>
                  <div className="actions-scheduling">
                    <Tooltip text="Pendente">
                      <button
                        onClick={() => handleStatusChange(visit, PENDING)}
                        className="icon-button-scheduling">
                        <Clock size={20} color="#cc4e00" />
                      </button>
                    </Tooltip>
                    <Tooltip text="Cancelado">
                      <button
                        onClick={() => handleStatusChange(visit, CANCELED)}
                        className="icon-button-scheduling">
                        <XCircle size={20} color="#d93535" />
                      </button>
                    </Tooltip>
                    <Tooltip text="Ausente">
                      <button
                        onClick={() => handleStatusChange(visit, NO_SHOW)}
                        className="icon-button-scheduling">
                        <UserX size={20} color="#9f0000" />
                      </button>
                    </Tooltip>
                    <Tooltip text="Concluído">
                      <button
                        onClick={() => handleStatusChange(visit, COMPLETED)}
                        className="icon-button-scheduling">
                        <CheckCircle size={20} color="#32b66b" />
                      </button>
                    </Tooltip>
                    <Tooltip text="Reagendado">
                      <button
                        onClick={() => handleStatusChange(visit, RESCHEDULED)}
                        className="icon-button-scheduling">
                        <CalendarClock size={20} color="#8a6f27" />
                      </button>
                    </Tooltip>
                    <Tooltip text="Inacessível">
                      <button
                        onClick={() => handleStatusChange(visit, UNREACHABLE)}
                        className="icon-button-scheduling">
                        <Ban size={20} color="#3a3a3a" />
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showRescheduleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Reagendar visita</h2>
            <div className="form-group">
              <label className="label-reschedule">Nova data:</label>
              <input
                type="date"
                required
                value={rescheduleForm.date}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label-reschedule">Novo Horário:</label>
              <input
                type="time"
                required
                value={rescheduleForm.time}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, time: e.target.value })}
              />
            </div>
            <div className="modal-buttons-send-email">
              <button onClick={() => setShowRescheduleModal(false)} className="cancel-button-send-email">Cancelar</button>
              <button onClick={handleReschedule} className="send-button-send-email">Reagendar</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default Scheduling;

